import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  getDoc,
  Timestamp,
  limit
} from 'firebase/firestore';
import { sendPasswordResetEmail, updatePassword } from 'firebase/auth';
import { db, auth } from './firebase';

/**
 * Comprehensive User Service for managing users, invitations, and sessions
 * Handles all user-related operations with proper validation and error handling
 */

// ===== USER MANAGEMENT FUNCTIONS =====

/**
 * Fetches all registered users (excludes pending invitations)
 * @returns {Promise<Array>} Array of user objects
 */
export const getAllUsers = async () => {
  try {
    // Use simple query without composite index requirement
    const q = query(collection(db, 'users'));
    
    const querySnapshot = await getDocs(q);
    let users = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter out invited users and sort manually to avoid index requirements
    users = users
      .filter(user => user.status !== 'invited')
      .sort((a, b) => {
        // Sort by createdAt descending
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });
    
    // Sync with IndexedDB for local search
    await syncUsersToIndexedDB(users);
    
    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw new Error('Error al cargar los usuarios');
  }
};

/**
 * Updates a user's role with validation
 * @param {string} userId - User ID to update
 * @param {string} newRole - New role to assign
 * @param {string} currentUserId - ID of user making the change
 * @returns {Promise<boolean>} Success status
 */
export const updateUserRole = async (userId, newRole, currentUserId) => {
  try {
    // Validate role
    if (!validateRolePermissions(newRole)) {
      throw new Error('Rol inválido');
    }
    
    // Check if current user can change this role
    if (!(await canChangeRole(currentUserId, userId, newRole))) {
      throw new Error('No tienes permisos para cambiar este rol');
    }
    
    // Check if this is the last admin
    if (await isLastAdmin(userId)) {
      throw new Error('No puedes cambiar el rol del último administrador');
    }
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: newRole,
      updatedAt: Timestamp.now(),
      updatedBy: currentUserId
    });
    
    // Audit the action
    await auditUserAction('role_change', userId, {
      oldRole: (await getDoc(userRef)).data().role,
      newRole,
      changedBy: currentUserId
    });
    
    return true;
  } catch (error) {
    console.error('Error updating user role:', error);
    throw new Error(error.message || 'Error al actualizar el rol del usuario');
  }
};

/**
 * Deactivates a user account
 * @param {string} userId - User ID to deactivate
 * @param {string} currentUserId - ID of user making the change
 * @returns {Promise<boolean>} Success status
 */
export const deactivateUser = async (userId, currentUserId) => {
  try {
    // Check if this is the last admin
    if (await isLastAdmin(userId)) {
      throw new Error('No puedes desactivar el último administrador');
    }
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status: 'inactive',
      updatedAt: Timestamp.now(),
      deactivatedBy: currentUserId,
      deactivatedAt: Timestamp.now()
    });
    
    // Force logout all sessions for this user
    await forceUserLogout(userId);
    
    // Audit the action
    await auditUserAction('user_deactivated', userId, {
      deactivatedBy: currentUserId
    });
    
    return true;
  } catch (error) {
    console.error('Error deactivating user:', error);
    throw new Error(error.message || 'Error al desactivar el usuario');
  }
};

/**
 * Activates a user account
 * @param {string} userId - User ID to activate
 * @param {string} currentUserId - ID of user making the change
 * @returns {Promise<boolean>} Success status
 */
export const activateUser = async (userId, currentUserId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status: 'active',
      updatedAt: Timestamp.now(),
      activatedBy: currentUserId,
      activatedAt: Timestamp.now()
    });
    
    // Audit the action
    await auditUserAction('user_activated', userId, {
      activatedBy: currentUserId
    });
    
    return true;
  } catch (error) {
    console.error('Error activating user:', error);
    throw new Error('Error al activar el usuario');
  }
};

/**
 * Forces logout for all sessions of a specific user
 * @param {string} userId - User ID to logout
 * @returns {Promise<boolean>} Success status
 */
export const forceUserLogout = async (userId) => {
  try {
    // Import session service functions
    const { endAllUserSessions } = await import('./sessionService');
    
    // End all user sessions
    const success = await endAllUserSessions(userId);
    
    // Audit the action
    await auditUserAction('forced_logout', userId, {
      success
    });
    
    return success;
  } catch (error) {
    console.error('Error forcing user logout:', error);
    throw new Error('Error al cerrar las sesiones del usuario');
  }
};

/**
 * Triggers password reset email for a user
 * @param {string} email - User email
 * @returns {Promise<boolean>} Success status
 */
export const resetUserPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    
    // Audit the action
    await auditUserAction('password_reset_sent', null, {
      email
    });
    
    return true;
  } catch (error) {
    console.error('Error sending password reset:', error);
    throw new Error('Error al enviar el correo de restablecimiento de contraseña');
  }
};

/**
 * Gets detailed user profile information
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User profile data
 */
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      throw new Error('Usuario no encontrado');
    }
    
    const userData = { id: userDoc.id, ...userDoc.data() };
    
    return {
      ...userData,
      recentSessions: []
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw new Error('Error al cargar el perfil del usuario');
  }
};

// ===== INVITATION MANAGEMENT FUNCTIONS =====

/**
 * Creates a new invitation with secure token
 * @param {string} email - Invitation email
 * @param {string} role - User role for invitation
 * @param {string} createdBy - ID of user creating invitation
 * @returns {Promise<Object>} Created invitation data
 */
export const createInvitation = async (email, role, createdBy) => {
  try {
    // Validate email format
    if (!isValidEmail(email)) {
      throw new Error('Email inválido');
    }
    
    // Validate role
    if (!validateRolePermissions(role)) {
      throw new Error('Rol inválido');
    }
    
    // Check if invitation already exists
    const existingQuery = query(
      collection(db, 'invitations'),
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error('Ya existe una invitación pendiente para este email');
    }
    
    // Check if user already exists
    const userQuery = query(
      collection(db, 'users'),
      where('email', '==', email.toLowerCase())
    );
    
    const userSnapshot = await getDocs(userQuery);
    if (!userSnapshot.empty) {
      throw new Error('Ya existe un usuario con este email');
    }
    
    // Generate secure token
    const token = generateInvitationToken();
    
    // Create invitation
    const invitationData = {
      email: email.toLowerCase(),
      role,
      token,
      status: 'pending',
      createdAt: Timestamp.now(),
      createdBy,
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
      usedAt: null,
      usedBy: null
    };
    
    const docRef = await addDoc(collection(db, 'invitations'), invitationData);
    
    // Audit the action
    await auditUserAction('invitation_created', null, {
      email,
      role,
      invitationId: docRef.id,
      createdBy
    });
    
    return {
      id: docRef.id,
      ...invitationData
    };
  } catch (error) {
    console.error('Error creating invitation:', error);
    throw new Error(error.message || 'Error al crear la invitación');
  }
};

/**
 * Fetches all invitations with status information
 * @returns {Promise<Array>} Array of invitation objects
 */
export const getAllInvitations = async () => {
  try {
    const q = query(
      collection(db, 'invitations'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const invitations = querySnapshot.docs.map(doc => {
      const data = { id: doc.id, ...doc.data() };
      
      // Check if expired
      const now = new Date();
      const expiresAt = data.expiresAt.toDate();
      if (now > expiresAt && data.status === 'pending') {
        data.status = 'expired';
      }
      
      return data;
    });
    
    return invitations;
  } catch (error) {
    console.error('Error fetching invitations:', error);
    throw new Error('Error al cargar las invitaciones');
  }
};

/**
 * Deletes an invitation
 * @param {string} invitationId - Invitation ID to delete
 * @param {string} deletedBy - ID of user deleting invitation
 * @returns {Promise<boolean>} Success status
 */
export const deleteInvitation = async (invitationId, deletedBy) => {
  try {
    // Get invitation data for audit
    const invitationDoc = await getDoc(doc(db, 'invitations', invitationId));
    if (!invitationDoc.exists()) {
      throw new Error('Invitación no encontrada');
    }
    
    const invitationData = invitationDoc.data();
    
    await deleteDoc(doc(db, 'invitations', invitationId));
    
    // Audit the action
    await auditUserAction('invitation_deleted', null, {
      invitationId,
      email: invitationData.email,
      deletedBy
    });
    
    return true;
  } catch (error) {
    console.error('Error deleting invitation:', error);
    throw new Error('Error al eliminar la invitación');
  }
};

/**
 * Resends an invitation with new token and extended expiry
 * @param {string} invitationId - Invitation ID to resend
 * @param {string} resendBy - ID of user resending invitation
 * @returns {Promise<Object>} Updated invitation data
 */
export const resendInvitation = async (invitationId, resendBy) => {
  try {
    const newToken = generateInvitationToken();
    const newExpiresAt = Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
    
    const invitationRef = doc(db, 'invitations', invitationId);
    await updateDoc(invitationRef, {
      token: newToken,
      expiresAt: newExpiresAt,
      status: 'pending',
      updatedAt: Timestamp.now(),
      resendBy,
      resendAt: Timestamp.now()
    });
    
    // Get updated data
    const updatedDoc = await getDoc(invitationRef);
    const updatedData = { id: updatedDoc.id, ...updatedDoc.data() };
    
    // Audit the action
    await auditUserAction('invitation_resent', null, {
      invitationId,
      resendBy
    });
    
    return updatedData;
  } catch (error) {
    console.error('Error resending invitation:', error);
    throw new Error('Error al reenviar la invitación');
  }
};

/**
 * Validates an invitation token
 * @param {string} token - Invitation token to validate
 * @returns {Promise<Object|null>} Invitation data if valid, null otherwise
 */
export const validateInvitation = async (token) => {
  try {
    const q = query(
      collection(db, 'invitations'),
      where('token', '==', token),
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const invitationDoc = querySnapshot.docs[0];
    const invitationData = { id: invitationDoc.id, ...invitationDoc.data() };
    
    // Check if expired
    const now = new Date();
    const expiresAt = invitationData.expiresAt.toDate();
    
    if (now > expiresAt) {
      // Mark as expired
      await updateDoc(invitationDoc.ref, {
        status: 'expired',
        updatedAt: Timestamp.now()
      });
      return null;
    }
    
    return invitationData;
  } catch (error) {
    console.error('Error validating invitation:', error);
    throw new Error('Error al validar la invitación');
  }
};

/**
 * Marks an invitation as used when user registers
 * @param {string} invitationId - Invitation ID
 * @param {string} userId - User ID who used the invitation
 * @returns {Promise<boolean>} Success status
 */
export const markInvitationAsUsed = async (invitationId, userId) => {
  try {
    const invitationRef = doc(db, 'invitations', invitationId);
    await updateDoc(invitationRef, {
      status: 'used',
      usedAt: Timestamp.now(),
      usedBy: userId,
      updatedAt: Timestamp.now()
    });
    
    // Audit the action
    await auditUserAction('invitation_used', userId, {
      invitationId
    });
    
    return true;
  } catch (error) {
    console.error('Error marking invitation as used:', error);
    throw new Error('Error al marcar la invitación como usada');
  }
};

// ===== VALIDATION AND SECURITY FUNCTIONS =====

/**
 * Checks if user is the last admin in the system
 * @param {string} userId - User ID to check
 * @returns {Promise<boolean>} True if user is last admin
 */
export const isLastAdmin = async (userId) => {
  try {
    const q = query(
      collection(db, 'users'),
      where('role', '==', 'admin'),
      where('status', '==', 'active')
    );
    
    const querySnapshot = await getDocs(q);
    const adminUsers = querySnapshot.docs.map(doc => doc.id);
    
    return adminUsers.length === 1 && adminUsers.includes(userId);
  } catch (error) {
    console.error('Error checking if last admin:', error);
    return false;
  }
};

/**
 * Validates if current user can change target user's role
 * @param {string} currentUserId - Current user ID
 * @param {string} targetUserId - Target user ID
 * @param {string} newRole - New role to assign
 * @returns {Promise<boolean>} True if change is allowed
 */
export const canChangeRole = async (currentUserId, targetUserId, newRole) => {
  try {
    // Get current user's role
    const currentUserDoc = await getDoc(doc(db, 'users', currentUserId));
    if (!currentUserDoc.exists()) return false;
    
    const currentUserRole = currentUserDoc.data().role;
    
    // Only admins can change roles
    if (currentUserRole !== 'admin') return false;
    
    // Admins can't change their own role if they're the last admin
    if (currentUserId === targetUserId && newRole !== 'admin') {
      return !(await isLastAdmin(currentUserId));
    }
    
    return true;
  } catch (error) {
    console.error('Error checking role change permission:', error);
    return false;
  }
};

/**
 * Generates a secure invitation token
 * @returns {string} Secure token
 */
export const generateInvitationToken = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return token;
};

/**
 * Validates role permissions
 * @param {string} role - Role to validate
 * @returns {boolean} True if role is valid
 */
export const validateRolePermissions = (role) => {
  const validRoles = ['admin', 'shopkeeper', 'solo_lectura'];
  return validRoles.includes(role);
};

/**
 * Logs user management actions for audit trail
 * @param {string} action - Action type
 * @param {string} targetUserId - Target user ID
 * @param {Object} details - Action details
 * @returns {Promise<boolean>} Success status
 */
export const auditUserAction = async (action, targetUserId, details) => {
  try {
    await addDoc(collection(db, 'audit_logs'), {
      action,
      targetUserId,
      details,
      timestamp: Timestamp.now(),
      performedBy: auth.currentUser?.uid || 'system'
    });
    
    return true;
  } catch (error) {
    console.error('Error logging audit action:', error);
    return false; // Don't throw error for audit logging
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Returns Spanish display name for role
 * @param {string} role - Role identifier
 * @returns {string} Spanish role name
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    admin: 'Administrador',
    shopkeeper: 'Shopkeeper',
    solo_lectura: 'Solo Lectura'
  };
  
  return roleNames[role] || role;
};

/**
 * Formats user status information
 * @param {Object} user - User object
 * @returns {Object} Formatted status info
 */
export const formatUserStatus = (user) => {
  const status = {
    statusText: user.status === 'active' ? 'Activo' : 'Inactivo',
    statusColor: user.status === 'active' ? 'green' : 'red',
    lastLogin: user.lastLogin ? formatDateTime(user.lastLogin) : 'Nunca',
    createdAt: formatDateTime(user.createdAt)
  };
  
  return status;
};

/**
 * Calculates human-readable session duration
 * @param {Date|Timestamp} loginTime - Session start time
 * @returns {string} Formatted duration
 */
export const calculateSessionDuration = (loginTime) => {
  const now = new Date();
  const login = loginTime instanceof Date ? loginTime : loginTime.toDate();
  const diffMs = now - login;
  
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHours > 0) {
    return `${diffHours}h ${diffMinutes}m`;
  }
  return `${diffMinutes}m`;
};

/**
 * Filters users by specific role
 * @param {Array} users - Array of user objects
 * @param {string} role - Role to filter by
 * @returns {Array} Filtered users
 */
export const getUsersByRole = (users, role) => {
  return users.filter(user => user.role === role);
};

/**
 * Local search functionality for users
 * @param {Array} users - Array of user objects
 * @param {string} query - Search query
 * @returns {Array} Filtered users
 */
export const searchUsers = (users, query) => {
  if (!query.trim()) return users;
  
  const searchTerm = query.toLowerCase();
  return users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm) ||
    user.displayName?.toLowerCase().includes(searchTerm) ||
    getRoleDisplayName(user.role).toLowerCase().includes(searchTerm)
  );
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Formats date/time to Spanish locale
 * @param {Date|Timestamp} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateTime = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = date instanceof Date ? date : date.toDate();
  return dateObj.toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formats date to Spanish format (DD/MMM/YYYY)
 * @param {Date|Timestamp} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = date instanceof Date ? date : date.toDate();
  const months = [
    'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
    'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
  ];
  
  const day = dateObj.getUTCDate().toString().padStart(2, '0');
  const month = months[dateObj.getUTCMonth()];
  const year = dateObj.getUTCFullYear();
  
  return `${day}/${month}/${year}`;
};

// ===== ANALYTICS AND REPORTING FUNCTIONS =====

/**
 * Gets user registration analytics for dashboard
 * @param {number} days - Number of days to look back
 * @returns {Promise<Object>} Analytics data
 */
export const getUserRegistrationAnalytics = async (days = 30) => {
  try {
    const users = await getAllUsers();
    const now = new Date();
    const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    
    const registrationsByDay = {};
    const roleDistribution = { admin: 0, shopkeeper: 0, solo_lectura: 0 };
    
    users.forEach(user => {
      const createdAt = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
      
      if (createdAt >= startDate) {
        const dayKey = createdAt.toISOString().split('T')[0];
        registrationsByDay[dayKey] = (registrationsByDay[dayKey] || 0) + 1;
      }
      
      roleDistribution[user.role] = (roleDistribution[user.role] || 0) + 1;
    });
    
    return {
      registrationsByDay,
      roleDistribution,
      totalUsers: users.length,
      recentUsers: users.filter(user => {
        const createdAt = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
        return createdAt >= startDate;
      }).length
    };
  } catch (error) {
    console.error('Error getting user registration analytics:', error);
    throw new Error('Error al obtener analíticas de registro');
  }
};

/**
 * Gets user activity analytics
 * @returns {Promise<Object>} Activity analytics
 */
export const getUserActivityAnalytics = async () => {
  try {
    const users = await getAllUsers();
    
    // Get actual session data from the session service
    const { getAllActiveSessions } = await import('./sessionService');
    const sessions = await getAllActiveSessions();
    
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const activeUsers = users.filter(user => {
      if (!user.lastLogin) return false;
      const lastLogin = user.lastLogin.toDate ? user.lastLogin.toDate() : new Date(user.lastLogin);
      return lastLogin >= thirtyDaysAgo;
    });
    
    const inactiveUsers = users.filter(user => {
      if (!user.lastLogin) return true;
      const lastLogin = user.lastLogin.toDate ? user.lastLogin.toDate() : new Date(user.lastLogin);
      return lastLogin < thirtyDaysAgo;
    });
    
    return {
      totalUsers: users.length,
      activeUsers: activeUsers.length,
      inactiveUsers: inactiveUsers.length,
      totalSessions: sessions.length,
      usersByRole: users.reduce((acc, user) => {
        acc[user.role] = (acc[user.role] || 0) + 1;
        return acc;
      }, { admin: 0, shopkeeper: 0, solo_lectura: 0 })
    };
  } catch (error) {
    console.error('Error getting user activity analytics:', error);
    throw new Error('Error al obtener analíticas de actividad');
  }
};

/**
 * Gets security alerts for admin dashboard
 * @returns {Promise<Object>} Security alerts
 */
export const getSecurityAlerts = async () => {
  try {
    const users = await getAllUsers();
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const inactiveUsers = users.filter(user => {
      if (!user.lastLogin) return true;
      const lastLogin = user.lastLogin.toDate ? user.lastLogin.toDate() : new Date(user.lastLogin);
      return lastLogin < thirtyDaysAgo;
    });
    
    // Mock data for other security alerts - in real app, you'd track these
    const suspiciousLogins = [];
    const passwordResets = [];
    const multipleFailedAttempts = [];
    
    return {
      inactiveUsers: inactiveUsers.length,
      suspiciousLogins: suspiciousLogins.length,
      passwordResets: passwordResets.length,
      multipleFailedAttempts: multipleFailedAttempts.length,
      details: {
        inactiveUsers: inactiveUsers.slice(0, 10), // Return first 10 for display
        suspiciousLogins,
        passwordResets,
        multipleFailedAttempts
      }
    };
  } catch (error) {
    console.error('Error getting security alerts:', error);
    throw new Error('Error al obtener alertas de seguridad');
  }
};

/**
 * Exports user data to CSV format
 * @param {Array} users - Users to export
 * @param {string} type - Export type ('users' or 'invitations')
 * @returns {string} CSV content
 */
export const exportUsersToCSV = (users, type = 'users') => {
  try {
    const headers = type === 'users' 
      ? ['Nombre', 'Email', 'Rol', 'Estado', 'Fecha Registro', 'Último Acceso']
      : ['Email', 'Rol', 'Estado', 'Fecha Creación', 'Fecha Expiración'];
    
    const rows = users.map(user => {
      if (type === 'users') {
        return [
          user.displayName || 'Sin nombre',
          user.email || '',
          getRoleDisplayName(user.role),
          user.status === 'active' ? 'Activo' : 'Inactivo',
          formatDate(user.createdAt),
          formatDateTime(user.lastLogin)
        ];
      } else {
        return [
          user.email || '',
          getRoleDisplayName(user.role),
          user.status === 'pending' ? 'Pendiente' : 
          user.status === 'used' ? 'Usado' : 'Expirado',
          formatDate(user.createdAt),
          formatDate(user.expiresAt)
        ];
      }
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');
    
    return csvContent;
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw new Error('Error al exportar datos');
  }
};

/**
 * Gets recent user activity for dashboard
 * @param {number} limit - Number of activities to return
 * @returns {Promise<Array>} Recent activities
 */
export const getRecentUserActivity = async (limit = 10) => {
  try {
    const [users, invitations] = await Promise.all([
      getAllUsers(),
      getAllInvitations()
    ]);
    
    const activities = [];
    
    // Add recent user registrations
    users.forEach(user => {
      activities.push({
        type: 'user_registered',
        userId: user.id,
        userEmail: user.email,
        userName: user.displayName,
        timestamp: user.createdAt,
        details: { role: user.role }
      });
    });
    
    // Add recent invitations
    invitations.forEach(invitation => {
      activities.push({
        type: 'invitation_created',
        invitationId: invitation.id,
        email: invitation.email,
        timestamp: invitation.createdAt,
        details: { role: invitation.role }
      });
    });
    
    // Sort by timestamp and limit
    activities.sort((a, b) => {
      const timestampA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const timestampB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return timestampB - timestampA;
    });
    
    return activities.slice(0, limit);
  } catch (error) {
    console.error('Error getting recent user activity:', error);
    throw new Error('Error al obtener actividad reciente');
  }
};

// ===== INDEXEDDB SYNC FUNCTIONS =====

/**
 * Syncs users data to IndexedDB for local search
 * @param {Array} users - Users to sync
 * @returns {Promise<boolean>} Success status
 */
const syncUsersToIndexedDB = async (users) => {
  try {
    // Open IndexedDB with version 21 to match other services
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        // Create all required object stores
        if (!db.objectStoreNames.contains('users')) {
          db.createObjectStore('users', { keyPath: 'uid' });
        }
        if (!db.objectStoreNames.contains('orders')) {
          db.createObjectStore('orders', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('clients')) {
          db.createObjectStore('clients', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('technicians')) {
          db.createObjectStore('technicians', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('equipment_catalog')) {
          db.createObjectStore('equipment_catalog', { keyPath: 'id' });
        }
      };
      
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('users')) {
          // If we still don't have the users store, try to create it
          db.close();
          resolve(false);
          return;
        }
        
        const transaction = db.transaction(['users'], 'readwrite');
        const store = transaction.objectStore('users');
        
        // Clear existing data
        store.clear();
        
        // Add new data
        users.forEach(user => {
          store.put(user);
        });
        
        transaction.oncomplete = () => resolve(true);
        transaction.onerror = () => reject(transaction.error);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        // Create all required object stores
        if (!db.objectStoreNames.contains('users')) {
          db.createObjectStore('users', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('orders')) {
          db.createObjectStore('orders', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('clients')) {
          db.createObjectStore('clients', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('technicians')) {
          db.createObjectStore('technicians', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('equipment_catalog')) {
          db.createObjectStore('equipment_catalog', { keyPath: 'id' });
        }
      };
    });
  } catch (error) {
    console.error('Error syncing to IndexedDB:', error);
    return false;
  }
};